import React, { useContext, useCallback, useMemo } from 'react';
import { Callout, DirectionalHint, INavLink } from '@fluentui/react';
import { useBoolean, useId } from '@fluentui/react-hooks';
import { DefaultButton } from 'office-ui-fabric-react';
import { registerIcons } from '@uifabric/styling';
import { Persona, PersonaSize, PersonaPresence } from 'office-ui-fabric-react/lib/Persona';
import { Nav, INavStyles, INavLinkGroup } from '@fluentui/react/lib/Nav';
import { serverHost } from "../config";
import Auth from '@/Auth/Auth';
import { ThemeContext } from '@fluentui/react-theme-provider';
import { useHistory } from 'react-router';
import { PersonComponent } from './PersonStatusStyles';

type Props = { user: any };
type NavLinkClickHandler = (ev?: React.MouseEvent<HTMLElement>, item?: INavLink) => void;

const NAV_STYLES: Partial<INavStyles> =
{
    root:
    {
        boxSizing: 'border-box',
        border: '1px solid #eee',
        overflowY: 'auto',
    },
    link:
    {
        whiteSpace: 'normal',
        lineHeight: 'inherit',
    },
    chevronIcon:
    {
        fontSize: "20px"
    }
};

const buildNavLinkGroups = (doLogout: NavLinkClickHandler): INavLinkGroup[] =>
[
    {
        links:
        [
            {
                key: '/identities',
                icon: 'identities',
                name: 'Gestión de Intervinientes',
                url: '/identities'
            },
            {
                key: '/profile/contract/frameworkAgreement',
                icon: 'frameworkAgreement',
                name: 'Condiciones Generales',
                url: '/profile/contract/frameworkAgreement'
            },
            {
                key: '/profile/documentation',
                icon: 'ironiaDocumentation',
                name: 'Documentación',
                url: '/profile/documentation'
            },
            {
                key: '/profile/ownership',
                iconProps: { iconName: 'profile' },
                name: 'Titularidad del contrato',
                url: '/profile/ownership'
            },
            {
                key: '/profile/subscription',
                iconProps: { iconName: 'finishedOperations' },
                name: 'Planes contratados',
                url: '/profile/subscription'
            },
            {
                key: '/password/change',
                iconProps: { iconName: 'lockOpen' },
                name: 'Cambiar contraseña y MFA',
                url: '/password/change',
                onClick: (ev) =>
                {
                    ev?.preventDefault();
                    window.location.href = serverHost + '/password/change';
                }
            },
            {
                key: '/profile/bank-accounts',
                iconProps: { iconName: 'bank2' },
                name: 'Cuentas Corrientes',
                url: '/profile/bank-accounts',
            },
            {
                key: '/logout',
                iconProps: { iconName: 'logout' },
                name: 'Cerrar sesión',
                url: '/logout',
                onClick: (ev) => ev && doLogout(ev)
            },
        ],
    },
];

const PersonStatus: React.FC<Props> = ({user}) =>
{
    registerIcons(
    {
        icons:
        {
            'identities': <i className="ironia-icon profile" />,
            'frameworkAgreement': <i className="ironia-icon receipt" />,
            'ironiaDocumentation': <i className="ironia-icon documentation" />,
            'profile': <i className="ironia-icon profile" />,
            'finishedOperations': <i className="ironia-icon finished-operations-2" />,
            'lockOpen': <i className="ironia-icon lock-open" />,
            'bank2': <i className="ironia-icon bank2" />,
            'information': <i className="ironia-icon information" />,
            'logout': <i className="ironia-icon logout" />
        }
    });

    const theme = useContext(ThemeContext);
    const history = useHistory();
    const [isCalloutVisible, { toggle: toggleIsCalloutVisible }] = useBoolean(false);
    const buttonId = useId('callout-button');
    const labelId = useId('callout-label');
    const descriptionId = useId('callout-description');

    const doLogout = useCallback<NavLinkClickHandler>(async (ev?: React.MouseEvent<HTMLElement>) =>
    {
        if (!ev) return;
        ev.preventDefault();
        await Auth.logout();
        window.location.replace(`${serverHost}/login`);
    }, []);

    const navLinkGroups = useMemo(() => buildNavLinkGroups(doLogout), [doLogout]);

    const handleNavLinkClick = (ev?: React.MouseEvent<HTMLElement>, item?: INavLink) =>
    {
        if(ev)
        {
            ev.nativeEvent.preventDefault();
        }
        if(item && item.url)
        {
            history.push(item.url);
            toggleIsCalloutVisible();
        }
    };

    const selectedKey = history.location.pathname.startsWith('/identities')
        ? '/identities'
        : history.location.pathname;

    const hasMemberCode = user.memberCode !== undefined && user.memberCode !== null && user.memberCode !== "";
    const personaSize = PersonaSize.size48;

    return(
        <PersonComponent className='persona-component' theme={theme}>
            <Persona imageInitials={user.initials}
                text={user.fullName}
                showSecondaryText={hasMemberCode}
                secondaryText={user.memberCode}
                size={PersonaSize.size32}
                presence={PersonaPresence.online}
            />
            <DefaultButton id={buttonId} onClick={toggleIsCalloutVisible}>
                <i className="ironia-icon keyboard-arrow-down" />
            </DefaultButton>
            {isCalloutVisible &&
            (
                <Callout ariaLabelledBy={labelId}
                    ariaDescribedBy={descriptionId}
                    role="alertdialog"
                    target={`#${buttonId}`}
                    onDismiss={toggleIsCalloutVisible}
                    setInitialFocus
                    gapSpace={-10}
                    calloutWidth={312}
                    directionalHint={DirectionalHint.bottomRightEdge}
                >
                    <Persona imageInitials={user.initials}
                        text={user.fullName}
                        showSecondaryText={hasMemberCode}
                        secondaryText={user.memberCode}
                        className="big header"
                        size={personaSize}
                    />
                    <Nav ariaLabel="Menú de usuario"
                        className="profile-menu"
                        styles={NAV_STYLES}
                        groups={navLinkGroups}
                        selectedKey={selectedKey}
                        onLinkClick={handleNavLinkClick}
                    />
                </Callout>
            )}
        </PersonComponent>
    );
};

export default PersonStatus;